import React, { useMemo, useRef } from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatColorText from '@mui/icons-material/FormatColorText';
import FormatColorFill from '@mui/icons-material/FormatColorFill';
import FormatLineSpacing from '@mui/icons-material/FormatLineSpacing';
import FormatSize from '@mui/icons-material/FormatSize';

export type FormattedText = {
  type: 'FormattedText';
  html: string;
};

type FormattedTextControl =
  | 'bold'
  | 'italic'
  | 'fontSize'
  | 'paragraph'
  | 'headers'
  | 'textColor'
  | 'backgroundColor'
  | 'lineSpacing'
  | 'paragraphSpacing';

type FormattedTextEditorProps = {
  value: string | FormattedText;
  onChange: (next: FormattedText) => void;
  controls?: FormattedTextControl[];
  minHeight?: number;
  readOnly?: boolean;
};

const DEFAULT_CONTROLS: FormattedTextControl[] = [
  'bold',
  'italic',
  'fontSize',
  'paragraph',
  'headers',
  'textColor',
  'backgroundColor',
  'lineSpacing',
  'paragraphSpacing',
];

const FONT_SIZE_PRESETS_PX = ['12', '14', '16', '18', '20', '24', '28', '32'] as const;

const LINE_SPACING_PRESETS = ['1.2', '1.4', '1.6', '1.8', '2'] as const;
const PARAGRAPH_SPACING_PRESETS = ['8', '12', '16', '20'] as const;

type BlockFormatValue = 'P' | 'H1' | 'H2' | 'H3';

const parseStoredHtmlMeta = (html: string): { lineSpacing: string | null; paragraphSpacing: string | null } => {
  if (!html?.trim()) return { lineSpacing: null, paragraphSpacing: null };
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  let lineSpacing: string | null = null;
  const formattedRoot = wrap.querySelector('[data-formatted-text]');
  if (formattedRoot) {
    const m = /line-height:\s*([\d.]+)/i.exec(formattedRoot.getAttribute('style') || '');
    if (m && (LINE_SPACING_PRESETS as readonly string[]).includes(m[1])) lineSpacing = m[1];
  }
  let paragraphSpacing: string | null = null;
  const firstP = wrap.querySelector('p');
  if (firstP) {
    const m = /margin-bottom:\s*(\d+)px/i.exec(firstP.getAttribute('style') || '');
    if (m && (PARAGRAPH_SPACING_PRESETS as readonly string[]).includes(m[1])) paragraphSpacing = m[1];
  }
  return { lineSpacing, paragraphSpacing };
};

const rgbToHex = (rgb: string): string | null => {
  const m = rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  const toHex = (n: string) => Number(n).toString(16).padStart(2, '0');
  return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`;
};

const normalizeColorForInput = (cssColor: string): string | null => {
  const t = cssColor.trim();
  if (t.startsWith('#')) {
    if (t.length === 4) {
      const r = t[1];
      const g = t[2];
      const b = t[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    return t.slice(0, 7);
  }
  return rgbToHex(t);
};

const pxFromFontSize = (fontSize: string): string | null => {
  const m = /^([\d.]+)px$/i.exec(fontSize.trim());
  if (!m) return null;
  return String(Math.round(parseFloat(m[1])));
};

const textNodesInRange = (range: Range): Text[] => {
  const out: Text[] = [];
  const root =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentElement
      : (range.commonAncestorContainer as HTMLElement);
  if (!root) return out;

  if (range.collapsed) {
    const n = range.startContainer;
    if (n.nodeType === Node.TEXT_NODE) out.push(n as Text);
    return out;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  while (node) {
    const t = node as Text;
    if (t.textContent && range.intersectsNode(t)) out.push(t);
    node = walker.nextNode();
  }
  return out;
};

const effectiveBackgroundColor = (el: HTMLElement): string => {
  let cur: HTMLElement | null = el;
  while (cur) {
    const bg = getComputedStyle(cur).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return bg;
    cur = cur.parentElement;
  }
  return getComputedStyle(document.documentElement).backgroundColor;
};

const getCaretElementForCollapsed = (range: Range, root: HTMLElement): HTMLElement | null => {
  const n = range.startContainer;
  if (n.nodeType === Node.TEXT_NODE) {
    const p = n.parentElement;
    return p && root.contains(p) ? p : null;
  }
  const el = n as HTMLElement;
  return root.contains(el) ? el : null;
};

/** When uniform across the selection (or caret), returns a preset px string; otherwise null. */
const getUniformFontSizePreset = (root: HTMLElement, range: Range): string | null => {
  if (range.collapsed) {
    const el = getCaretElementForCollapsed(range, root);
    if (!el) return null;
    const px = pxFromFontSize(getComputedStyle(el).fontSize);
    if (!px || !(FONT_SIZE_PRESETS_PX as readonly string[]).includes(px)) return null;
    return px;
  }
  const texts = textNodesInRange(range);
  if (texts.length === 0) return null;
  const sizes = new Set<string>();
  for (const t of texts) {
    const p = t.parentElement;
    if (!p || !root.contains(p)) continue;
    const px = pxFromFontSize(getComputedStyle(p).fontSize);
    if (px) sizes.add(px);
  }
  if (sizes.size !== 1) return null;
  const only = [...sizes][0];
  return (FONT_SIZE_PRESETS_PX as readonly string[]).includes(only) ? only : null;
};

const getUniformBlockFormat = (root: HTMLElement, range: Range): BlockFormatValue | null => {
  const blocks = root.querySelectorAll('p,h1,h2,h3');
  const tags = new Set<string>();
  for (let i = 0; i < blocks.length; i += 1) {
    const el = blocks[i];
    if (range.intersectsNode(el)) tags.add(el.tagName.toLowerCase());
  }
  if (tags.size !== 1) return null;
  const tag = [...tags][0];
  if (tag === 'p') return 'P';
  if (tag === 'h1') return 'H1';
  if (tag === 'h2') return 'H2';
  if (tag === 'h3') return 'H3';
  return null;
};

const getUniformForegroundHex = (root: HTMLElement, range: Range): string | null => {
  const sample = (el: HTMLElement): string | null => {
    const c = normalizeColorForInput(getComputedStyle(el).color);
    return c;
  };
  if (range.collapsed) {
    const el = getCaretElementForCollapsed(range, root);
    if (!el) return null;
    return sample(el);
  }
  const texts = textNodesInRange(range);
  if (texts.length === 0) return null;
  const colors = new Set<string>();
  for (const t of texts) {
    const p = t.parentElement;
    if (!p || !root.contains(p)) continue;
    const c = sample(p);
    if (c) colors.add(c.toLowerCase());
  }
  if (colors.size !== 1) return null;
  return [...colors][0];
};

const getUniformBackgroundHex = (root: HTMLElement, range: Range): string | null => {
  const sample = (el: HTMLElement): string | null => {
    const raw = effectiveBackgroundColor(el);
    return normalizeColorForInput(raw);
  };
  if (range.collapsed) {
    const el = getCaretElementForCollapsed(range, root);
    if (!el) return null;
    return sample(el);
  }
  const texts = textNodesInRange(range);
  if (texts.length === 0) return null;
  const colors = new Set<string>();
  for (const t of texts) {
    const p = t.parentElement;
    if (!p || !root.contains(p)) continue;
    const c = sample(p);
    if (c) colors.add(c.toLowerCase());
  }
  if (colors.size !== 1) return null;
  return [...colors][0];
};

const getLineSpacingFromSelection = (root: HTMLElement, range: Range): string | null => {
  let node: Node | null = range.commonAncestorContainer;
  while (node && node !== root) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset.formattedText === 'true') {
        const m = /line-height:\s*([\d.]+)/i.exec(el.getAttribute('style') || '');
        if (m && (LINE_SPACING_PRESETS as readonly string[]).includes(m[1])) return m[1];
        return null;
      }
    }
    node = node.parentNode;
  }
  return null;
};

const getUniformParagraphSpacing = (root: HTMLElement, range: Range): string | null => {
  const ps = root.querySelectorAll('p');
  const margins = new Set<string>();
  for (let i = 0; i < ps.length; i += 1) {
    const p = ps[i] as HTMLElement;
    if (!range.intersectsNode(p)) continue;
    const mb = getComputedStyle(p).marginBottom;
    const m = /^([\d.]+)px$/.exec(mb);
    if (m) margins.add(m[1].split('.')[0]);
  }
  if (margins.size !== 1) return null;
  const v = [...margins][0];
  return (PARAGRAPH_SPACING_PRESETS as readonly string[]).includes(v) ? v : null;
};

const extractHtml = (value: string | FormattedText): string => {
  if (typeof value === 'string') return value;
  return value?.html ?? '';
};

const withBlockStyles = (html: string, lineSpacing: string, paragraphSpacing: string): string => {
  const source = html || '<p></p>';
  const withParagraphSpacing = source.replace(/<p(?![^>]*style=)([^>]*)>/gi, `<p$1 style="margin-bottom:${paragraphSpacing}px;">`);
  return `<div data-formatted-text="true" style="line-height:${lineSpacing};">${withParagraphSpacing}</div>`;
};

const safeQueryCommandState = (command: string): boolean => {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
};

const applyBlockFormatToHtml = (html: string, blockFormat: BlockFormatValue): string => {
  if (!html) return `<${blockFormat.toLowerCase()}></${blockFormat.toLowerCase()}>`;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const firstBlock = doc.body.firstElementChild;
  if (!firstBlock) {
    return `<${blockFormat.toLowerCase()}>${doc.body.textContent || ''}</${blockFormat.toLowerCase()}>`;
  }
  const replacement = doc.createElement(blockFormat.toLowerCase());
  replacement.innerHTML = firstBlock.innerHTML;
  firstBlock.replaceWith(replacement);
  return doc.body.innerHTML;
};

const FormattedTextEditor: React.FC<FormattedTextEditorProps> = ({
  value,
  onChange,
  controls = DEFAULT_CONTROLS,
  minHeight = 96,
  readOnly = false,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  /** Last range wholly inside the editor; used to restore selection after toolbar clicks. */
  const savedRangeRef = useRef<Range | null>(null);
  const initialHtml = useMemo(() => extractHtml(value), [value]);
  const [lineSpacing, setLineSpacing] = React.useState('1.4');
  const [paragraphSpacing, setParagraphSpacing] = React.useState('12');
  const [textColor, setTextColor] = React.useState('#111111');
  const [backgroundColor, setBackgroundColor] = React.useState('#ffffff');
  const [blockFormat, setBlockFormat] = React.useState<BlockFormatValue | ''>('P');
  const [fontSizePreset, setFontSizePreset] = React.useState('');
  const [boldActive, setBoldActive] = React.useState(false);
  const [italicActive, setItalicActive] = React.useState(false);
  const toolbarSyncRafRef = useRef<number | null>(null);

  const emitChange = (overrides?: { lineSpacing?: string; paragraphSpacing?: string }) => {
    const html = editorRef.current?.innerHTML ?? initialHtml ?? '<p></p>';
    const ls = overrides?.lineSpacing ?? lineSpacing;
    const ps = overrides?.paragraphSpacing ?? paragraphSpacing;
    onChange({
      type: 'FormattedText',
      html: withBlockStyles(html, ls, ps),
    });
  };

  const rangeInsideEditor = (range: Range, root: HTMLElement) => (
    root.contains(range.commonAncestorContainer)
  );

  const syncToolbarFromSelection = React.useCallback(() => {
    if (readOnly) return;
    const root = editorRef.current;
    if (!root) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!rangeInsideEditor(range, root)) return;

    if (controls.includes('bold')) setBoldActive(safeQueryCommandState('bold'));
    if (controls.includes('italic')) setItalicActive(safeQueryCommandState('italic'));
    if (controls.includes('fontSize')) {
      const next = getUniformFontSizePreset(root, range);
      setFontSizePreset(next ?? '');
    }
    if (controls.includes('paragraph') || controls.includes('headers')) {
      const bf = getUniformBlockFormat(root, range);
      setBlockFormat(bf ?? '');
    }
    if (controls.includes('textColor')) {
      const fg = getUniformForegroundHex(root, range);
      if (fg) setTextColor(fg);
    }
    if (controls.includes('backgroundColor')) {
      const bg = getUniformBackgroundHex(root, range);
      if (bg) setBackgroundColor(bg);
    }
    if (controls.includes('lineSpacing')) {
      const ls = getLineSpacingFromSelection(root, range);
      if (ls) setLineSpacing(ls);
    }
    if (controls.includes('paragraphSpacing')) {
      const ps = getUniformParagraphSpacing(root, range);
      if (ps) setParagraphSpacing(ps);
    }
  }, [readOnly, controls]);

  const saveSelectionFromWindow = () => {
    const root = editorRef.current;
    if (!root) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const r = sel.getRangeAt(0);
    if (!rangeInsideEditor(r, root)) return;
    savedRangeRef.current = r.cloneRange();
  };

  const restoreSavedSelection = (): boolean => {
    const root = editorRef.current;
    const saved = savedRangeRef.current;
    if (!root || !saved) return false;
    try {
      if (!rangeInsideEditor(saved, root)) {
        savedRangeRef.current = null;
        return false;
      }
      const sel = window.getSelection();
      if (!sel) return false;
      sel.removeAllRanges();
      sel.addRange(saved);
      return true;
    } catch {
      savedRangeRef.current = null;
      return false;
    }
  };

  /**
   * Toolbar controls steal focus from contenteditable by default, which collapses the text
   * selection so execCommand targets the wrong range. Prevent default on pointer down so the
   * editor keeps focus and the current selection until the click runs.
   */
  const toolbarPointerDown = (e: React.PointerEvent) => {
    if (readOnly) return;
    e.preventDefault();
  };

  const run = (command: string, commandValue?: string) => {
    if (readOnly) return;
    restoreSavedSelection();
    editorRef.current?.focus();
    restoreSavedSelection();
    if (command === 'foreColor' || command === 'hiliteColor') {
      // Ensure inline style output for color commands across Chromium variants.
      document.execCommand('styleWithCSS', false, 'true');
    }
    const nextValue = command === 'formatBlock' && commandValue
      ? `<${commandValue.toLowerCase()}>`
      : commandValue;
    document.execCommand(command, false, nextValue);
    emitChange();
    requestAnimationFrame(() => {
      syncToolbarFromSelection();
    });
  };

  const applyTextColor = (nextColor: string) => {
    setTextColor(nextColor);
    if (readOnly || !editorRef.current) return;
    restoreSavedSelection();
    editorRef.current.focus();
    restoreSavedSelection();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('foreColor', false, nextColor);
    emitChange();
  };

  const applyBackgroundColor = (nextColor: string) => {
    setBackgroundColor(nextColor);
    if (readOnly || !editorRef.current) return;
    restoreSavedSelection();
    editorRef.current.focus();
    restoreSavedSelection();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('hiliteColor', false, nextColor);
    emitChange();
  };

  /** Applies pixel font size to the current non-collapsed selection. Inline-only ranges get a span; block nodes get style on the block. */
  const applyFontSizeToSelection = (px: string) => {
    if (readOnly || !editorRef.current) return;
    const root = editorRef.current;
    restoreSavedSelection();
    root.focus();
    restoreSavedSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    if (!root.contains(range.commonAncestorContainer)) return;

    const fragment = range.extractContents();
    const blockSelector = 'p,h1,h2,h3,h4,h5,h6,li';
    const blockEls = fragment.querySelectorAll(blockSelector);
    if (blockEls.length > 0) {
      blockEls.forEach((el) => {
        (el as HTMLElement).style.fontSize = `${px}px`;
      });
      range.insertNode(fragment);
    } else {
      const span = document.createElement('span');
      span.style.fontSize = `${px}px`;
      span.appendChild(fragment);
      range.insertNode(span);
    }
    sel.removeAllRanges();
    emitChange();
    setFontSizePreset(px);
  };

  React.useEffect(() => {
    const onSelectionChange = () => {
      saveSelectionFromWindow();
      if (toolbarSyncRafRef.current != null) cancelAnimationFrame(toolbarSyncRafRef.current);
      toolbarSyncRafRef.current = requestAnimationFrame(() => {
        toolbarSyncRafRef.current = null;
        syncToolbarFromSelection();
      });
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      if (toolbarSyncRafRef.current != null) cancelAnimationFrame(toolbarSyncRafRef.current);
    };
  }, [syncToolbarFromSelection]);

  React.useEffect(() => {
    const parsed = parseStoredHtmlMeta(initialHtml);
    if (parsed.lineSpacing) setLineSpacing(parsed.lineSpacing);
    if (parsed.paragraphSpacing) setParagraphSpacing(parsed.paragraphSpacing);
  }, [initialHtml]);

  React.useEffect(() => {
    if (!editorRef.current) return;
    if (!editorRef.current.innerHTML) {
      editorRef.current.innerHTML = initialHtml || '<p></p>';
    }
    const id = requestAnimationFrame(() => {
      syncToolbarFromSelection();
    });
    return () => cancelAnimationFrame(id);
  }, [initialHtml, syncToolbarFromSelection]);

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        onPointerDownCapture={readOnly ? undefined : () => saveSelectionFromWindow()}
      >
        {!readOnly && controls.includes('bold') && (
          <Tooltip title="Bold">
            <IconButton
              size="small"
              aria-label="Toggle bold"
              color={boldActive ? 'primary' : 'default'}
              onPointerDown={toolbarPointerDown}
              onClick={() => run('bold')}
            >
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!readOnly && controls.includes('italic') && (
          <Tooltip title="Italic">
            <IconButton
              size="small"
              aria-label="Toggle italic"
              color={italicActive ? 'primary' : 'default'}
              onPointerDown={toolbarPointerDown}
              onClick={() => run('italic')}
            >
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!readOnly && controls.includes('fontSize') && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FormatSize fontSize="small" />
            <Select
              native
              size="small"
              value={fontSizePreset}
              onChange={(e) => {
                const px = String((e.target as HTMLSelectElement).value);
                if (!px) return;
                applyFontSizeToSelection(px);
              }}
              displayEmpty
              sx={{ minWidth: 104 }}
              inputProps={{
                'aria-label': 'Font size',
                'data-testid': 'formatted-text-font-size',
              }}
            >
              <option value="">Size</option>
              {FONT_SIZE_PRESETS_PX.map((n) => (
                <option key={n} value={n}>{`${n}px`}</option>
              ))}
            </Select>
          </Stack>
        )}
        {!readOnly && (controls.includes('paragraph') || controls.includes('headers')) && (
          <Select
            native
            size="small"
            displayEmpty
            value={blockFormat}
            onChange={(e) => {
              const next = e.target.value as BlockFormatValue | '';
              if (next === '') return;
              setBlockFormat(next);
              if (editorRef.current) {
                editorRef.current.innerHTML = applyBlockFormatToHtml(editorRef.current.innerHTML, next);
              }
              emitChange();
            }}
            sx={{ minWidth: 140 }}
            inputProps={{ 'aria-label': 'Block format', 'data-testid': 'formatted-text-block-format' }}
          >
            <option value="">Format</option>
            <option value="P">Paragraph</option>
            <option value="H1">Header 1</option>
            <option value="H2">Header 2</option>
            <option value="H3">Header 3</option>
          </Select>
        )}
        {!readOnly && controls.includes('textColor') && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FormatColorText fontSize="small" />
            <input
              type="color"
              value={textColor}
              onInput={(e) => applyTextColor((e.target as HTMLInputElement).value)}
              onChange={(e) => applyTextColor(e.target.value)}
              aria-label="Text color"
              data-testid="formatted-text-color"
            />
          </Stack>
        )}
        {!readOnly && controls.includes('backgroundColor') && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FormatColorFill fontSize="small" />
            <input
              type="color"
              value={backgroundColor}
              onInput={(e) => applyBackgroundColor((e.target as HTMLInputElement).value)}
              onChange={(e) => applyBackgroundColor(e.target.value)}
              aria-label="Background color"
              data-testid="formatted-text-background-color"
            />
          </Stack>
        )}
        {!readOnly && controls.includes('lineSpacing') && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FormatLineSpacing fontSize="small" />
            <Select
              size="small"
              value={lineSpacing}
              onChange={(e) => {
                const next = String(e.target.value);
                setLineSpacing(next);
                emitChange({ lineSpacing: next });
              }}
              sx={{ minWidth: 100 }}
              inputProps={{ 'aria-label': 'Line spacing' }}
            >
              <MenuItem value="1.2">1.2</MenuItem>
              <MenuItem value="1.4">1.4</MenuItem>
              <MenuItem value="1.6">1.6</MenuItem>
              <MenuItem value="1.8">1.8</MenuItem>
              <MenuItem value="2">2.0</MenuItem>
            </Select>
          </Stack>
        )}
        {!readOnly && controls.includes('paragraphSpacing') && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption">Paragraph spacing</Typography>
            <Select
              size="small"
              value={paragraphSpacing}
              onChange={(e) => {
                const next = String(e.target.value);
                setParagraphSpacing(next);
                emitChange({ paragraphSpacing: next });
              }}
              sx={{ minWidth: 90 }}
              inputProps={{ 'aria-label': 'Paragraph spacing' }}
            >
              <MenuItem value="8">8px</MenuItem>
              <MenuItem value="12">12px</MenuItem>
              <MenuItem value="16">16px</MenuItem>
              <MenuItem value="20">20px</MenuItem>
            </Select>
          </Stack>
        )}
      </Stack>
      <Box
        ref={editorRef}
        data-testid="formatted-text-editor"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={() => { if (!readOnly) emitChange(); }}
        onMouseUp={readOnly ? undefined : () => saveSelectionFromWindow()}
        onKeyUp={readOnly ? undefined : () => saveSelectionFromWindow()}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          minHeight,
          '&:focus': {
            outline: 'none',
            borderColor: readOnly ? 'divider' : 'primary.main',
          },
        }}
      />
    </Stack>
  );
};

export default FormattedTextEditor;
